import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { LogOut, KeyRound, Bell, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CambiarPasswordModal from '../components/admin/CambiarPasswordModal';
import SedeSelector from '../components/admin/SedeSelector';
import NotificationBell from '../components/NotificationBell';
import GreenNotificationBell from '../components/GreenNotificationBell';
import VioletNotificationBell from '../components/VioletNotificationBell';
import CrearNotificacionModal from '../components/admin/CrearNotificacionModal';
import ResumenDeudasWidget from '../modules/accounting/shared/ResumenDeudasWidget';

// Importar configuración central de navegación (FUENTE ÚNICA DE VERDAD)
import { MENU_PRINCIPAL } from '../config/navigation';

// ============ CODE SPLITTING - Lazy load de componentes pesados ============
// Componentes críticos (cargan inmediatamente)
const ClientsTab = lazy(() => import('../components/admin/ClientsTab'));
// Correos que ven la pestaña Planificación (misma lista que backend/routes/planificacion.py).
const PLANIFICADORES = ['anaandres@metodopilardominguez.com'];
const CuadranteTab = lazy(() => import('../components/admin/CuadranteTab'));
const PlanificacionTab = lazy(() => import('../components/admin/PlanificacionTab'));

// Componentes secundarios (cargan bajo demanda)
const HorariosFijosTab = lazy(() => import('../components/admin/HorariosFijosTab'));
const HorariosSemanalesTab = lazy(() => import('../components/admin/HorariosSemanalesTab'));
const CambiosPanel = lazy(() => import('../components/admin/CambiosPanel'));
const BloqueosTab = lazy(() => import('../components/admin/BloqueosTab'));
const BonificacionesTab = lazy(() => import('../components/admin/BonificacionesTab'));
const ClasesPruebaTab = lazy(() => import('../components/admin/ClasesPruebaTab'));
const ClienteEmpresaTab = lazy(() => import('../modules/accounting/income/ClienteEmpresaTab'));
// PR-CTRL-4 (Ann 2026-05-24) · vista oficial Controles = ControlesAutopistaTab.
// Legacy AltasBajasTab / DesactivadosTab / DatosClienteTab eliminados físicamente.
const ControlesAutopistaTab = lazy(() => import('../components/admin/controles/ControlesAutopistaTab'));
const HorariosDisponiblesTab = lazy(() => import('../components/admin/HorariosDisponiblesTab'));
const IntensidadesTab = lazy(() => import('../components/admin/IntensidadesTab'));
const SedesTab = lazy(() => import('../components/admin/SedesTab'));
const PaquetesContinuo8Tab = lazy(() => import('../components/admin/PaquetesContinuo8Tab'));
const ConfiguracionTab = lazy(() => import('../components/admin/ConfiguracionTab'));
const CuotasTab = lazy(() => import('../modules/accounting/income/CuotasTab'));
const DeudaPresencialPanel = lazy(() => import('../components/admin/DeudaPresencialPanel'));
const EstadisticasTab = lazy(() => import('../components/admin/EstadisticasTab'));
const ValidacionCajaTab = lazy(() => import('../modules/accounting/ledger/ValidacionCajaTab'));
const HojaContableTab = lazy(() => import('../modules/accounting/ledger/HojaContableTab'));
const ServiciosTab = lazy(() => import('../components/admin/ServiciosTab'));
const HistorialNotificacionesTab = lazy(() => import('../components/admin/HistorialNotificacionesTab'));
const ConfiguracionGeneralTab = lazy(() => import('../components/admin/ConfiguracionGeneralTab'));
const ConfiguracionClinicaTab = lazy(() => import('../components/admin/ConfiguracionClinicaTab'));
const DerivadoresTab = lazy(() => import('../components/admin/DerivadoresTab'));  // Médicos que derivan (Ann 03-11-2026)
const EmailMarketingTab = lazy(() => import('../components/admin/EmailMarketingTab'));
const EmailMarketingIATab = lazy(() => import('../components/admin/EmailMarketingIATab'));
const BonosTab = lazy(() => import('../components/admin/BonosTab'));
const ConsultasTab = lazy(() => import('../components/admin/ConsultasTab'));
const ComercialTab = lazy(() => import('../components/admin/ComercialTab'));
const ComercialV2Tab = lazy(() => import('../components/admin/ComercialV2Tab'));
const ClinicaTab = lazy(() => import('../components/admin/clinica/ClinicaTab'));
const PreciosConsultaTab = lazy(() => import('../modules/accounting/income/PreciosConsultaTab'));
const SecurityDashboard = lazy(() => import('../components/admin/SecurityDashboard'));
const LaboralTab = lazy(() => import('../components/admin/LaboralTab'));
const ContabilidadTab = lazy(() => import('../modules/accounting/ContabilidadTab'));
const TaquillasTab = lazy(() => import('../components/admin/TaquillasTab'));
const TecnicaTab = lazy(() => import('../components/admin/TecnicaTab'));
const ConfiguracionTecnicaTab = lazy(() => import('../components/admin/ConfiguracionTecnicaTab'));
const CanalVideosTab = lazy(() => import('../components/admin/CanalVideosTab'));
const FisioVisionTab = lazy(() => import('../components/admin/FisioVisionTab'));
const ContabilidadV2Tab = lazy(() => import('../modules/accounting/ContabilidadV2Tab'));
const ContableBasicoTab = lazy(() => import('../modules/accounting/ContableBasicoTab'));
const RolesTab = lazy(() => import('../modules/seguridad/RolesTab'));
const UsuariosTab = lazy(() => import('../modules/seguridad/UsuariosTab'));
const SeguridadAccesosTab = lazy(() => import('../components/admin/SeguridadAccesosTab'));
const MiPuestoTab = lazy(() => import('../components/admin/MiPuestoTab'));
const ControlCajaTab = lazy(() => import('../components/admin/ControlCajaTab'));

// Componente de Loading para Suspense
const TabLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      <span className="text-sm text-slate-500">Cargando...</span>
    </div>
  </div>
);

const AdminDashboard = ({ user, onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Leer parámetro 'tab' de la URL al inicializar. Si hay un ?paciente= en la
  // URL (ficha clínica abierta), restaurar la sección 'clinica' al recargar (F5)
  // aunque no venga ?tab=, para no "sacar fuera" al usuario.
  // COLABORADOR (Ann 2026-09-06): persona de fuera. No es staff y la API de
  // personal ya le responde 403; aquí además entra directo a lo único suyo y no
  // se le pinta ningún otro sitio. Es maquillaje encima de la cerradura, NO la
  // cerradura: esconder el menú por sí solo no cierra nada.
  const esColaborador = user?.role === 'colaborador';
  const initialTab = esColaborador
    ? 'comercial'
    : (searchParams.get('tab') || (searchParams.get('paciente') ? 'clinica' : 'clients'));
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNotificacionModal, setShowNotificacionModal] = useState(false);
  // Contador para forzar re-mount de ClinicaTab cada vez que se navega a ella
  const [clinicaKey, setClinicaKey] = useState(0);
  const { isAdmin, isEditor, sedeActiva, colorSede, hasTabPermission, hasPermission, sedes } = useAuth();

  // Función para cambiar de pestaña manteniendo posición de scroll
  const handleTabChange = (newTab) => {
    const scrollY = window.scrollY;

    // Anclar la pestaña activa a la URL (?tab=...) para que al RECARGAR (F5) o
    // cerrar un modal el CRM conserve la posición en vez de volver al inicio.
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    // Si salimos de la pestaña clinica, limpiar el parámetro 'paciente' de la URL
    // Esto garantiza que al volver a Clínica, se muestre el dashboard limpio
    if (activeTab === 'clinica' && newTab !== 'clinica') {
      newParams.delete('paciente');
    }
    setSearchParams(newParams, { replace: true });

    // Solo incrementar clinicaKey si venimos de OTRA pestaña hacia 'clinica'
    // Esto fuerza el re-mount de ClinicaTab solo al entrar desde fuera
    if (newTab === 'clinica' && activeTab !== 'clinica') {
      setClinicaKey(prev => prev + 1);
    }

    setActiveTab(newTab);

    // Restaurar posición después del re-render
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  };

  // Actualizar pestaña cuando cambia el parámetro 'tab' (o 'paciente') en la URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const pacienteParam = searchParams.get('paciente');
    const target = tabParam || (pacienteParam ? 'clinica' : null);
    // INCIDENTE 2026-09-07: el colaborador NO cambia de pestaña por URL (?tab=mi-puesto, ?tab=clients…);
    // solo existe Comercial para él. La cerradura real sigue en la API (403); esto evita pintar cascarones.
    if (esColaborador) return;
    if (target && target !== activeTab) {
      setActiveTab(target);
    }
  }, [searchParams]);

  // Usar la configuración central de navegación (FUENTE ÚNICA DE VERDAD)
  // Si cambias el menú en /src/config/navigation.js, se reflejará aquí automáticamente
  const allTabs = MENU_PRINCIPAL;

  // Filter tabs based on permissions
  const visibleTabs = allTabs.filter(tab => {
    if (tab.hidden) return false;
    // Al colaborador SOLO se le pinta su sección, ni siquiera las "siempre visibles".
    if (esColaborador) return tab.id === 'comercial';
    // Planificación (Ann 2026-10-20): «solo yo». Ni siquiera otro admin.
    if (tab.soloAnn) return isAdmin && PLANIFICADORES.includes(String(user?.email || '').toLowerCase());
    if (tab.alwaysVisible) return true;
    // Si tiene permiso directo o permiso alternativo
    if (hasTabPermission(tab.id)) return true;
    if (tab.altPermission) {
      if (Array.isArray(tab.altPermission)) {
        if (tab.altPermission.some(p => hasPermission(p, 'ver'))) return true;
      } else {
        if (hasPermission(tab.altPermission, 'ver')) return true;
      }
    }
    return false;
  });

  // Organize tabs into rows (max 6 per row for better display)
  const organizeTabsIntoRows = (tabs) => {
    const rows = [];
    const tabsPerRow = 6;

    for (let i = 0; i < tabs.length; i += tabsPerRow) {
      rows.push(tabs.slice(i, i + tabsPerRow));
    }

    return rows;
  };

  const tabRows = organizeTabsIntoRows(visibleTabs);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="text-sm text-gray-600">
                Bienvenido, {user.nombre}
                {isAdmin && <span className="ml-2 text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">Administrador</span>}
                {isEditor && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Editor</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Campana de notificaciones - según permiso */}
              {(isAdmin || hasPermission('header.campana', 'ver')) && (
                <NotificationBell />
              )}
              {/* Campanita VERDE: ventas/acciones de cliente (Canal) */}
              {(isAdmin || hasPermission('header.campana', 'ver')) && (
                <GreenNotificationBell />
              )}
              {/* Campana VIOLETA: usuarios online del Club (altas/bajas/impagos · Stripe) */}
              {(isAdmin || hasPermission('header.campana', 'ver')) && (
                <VioletNotificationBell />
              )}
              {/* Botón enviar notificación - según permiso */}
              {(isAdmin || hasPermission('header.campana.enviar', 'crear')) && (
                <Button
                  onClick={() => setShowNotificacionModal(true)}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  title="Enviar notificación a editores"
                >
                  <Bell className="w-4 h-4" />
                  <span className="hidden sm:inline">Enviar Notificación</span>
                </Button>
              )}
              <Button
                onClick={() => setShowPasswordModal(true)}
                variant="outline"
                className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                title="Cambiar contraseña"
              >
                <KeyRound className="w-4 h-4" />
                <span className="hidden sm:inline">Cambiar Contraseña</span>
              </Button>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Sede Selector */}
      <SedeSelector />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Widgets - Según permiso header.alertas */}
        {false && (isAdmin || hasPermission('header.alertas', 'ver')) && (
          <div className="mb-4">
            <ResumenDeudasWidget
              onFilterRecibosDevueltos={() => {
                setActiveTab('clients');
                localStorage.setItem('filterRecibosDevueltos', 'true');
              }}
            />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {/* Dynamic compact menu - only shows rows with visible tabs */}
          {visibleTabs.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg p-2">
              {tabRows.map((row, rowIndex) => (
                <TabsList
                  key={rowIndex}
                  className={`grid w-full gap-1 ${rowIndex < tabRows.length - 1 ? 'mb-2' : ''}`}
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(row.length, 6)}, minmax(0, 1fr))`
                  }}
                >
                  {row.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        data-testid={`${tab.id}-tab`}
                        className="flex items-center gap-2 justify-center"
                      >
                        {Icon && <Icon className="w-4 h-4" />}
                        <span className="hidden sm:inline">{tab.label}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              ))}
            </div>
          )}

          {/* Show message if no tabs are visible */}
          {visibleTabs.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <p className="text-yellow-800 font-medium">
                No tienes permisos para acceder a ninguna pestaña.
              </p>
              <p className="text-yellow-600 text-sm mt-2">
                Contacta con el administrador para solicitar permisos.
              </p>
            </div>
          )}

          {hasTabPermission('clients') && (
            <TabsContent value="clients" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ClientsTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('controles') && (
            <TabsContent value="controles" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ControlesAutopistaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('clases-prueba') && (
            <TabsContent value="clases-prueba" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ClasesPruebaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('intensidades') && (
            <TabsContent value="intensidades" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <IntensidadesTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('horarios-fijos') && (
            <TabsContent value="horarios-fijos" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <HorariosFijosTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('horarios-semanales') && (
            <TabsContent value="horarios-semanales" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <HorariosSemanalesTab user={user} />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('cambios') && (
            <TabsContent value="cambios" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <CambiosPanel />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('clientes-empresa') && (
            <TabsContent value="clientes-empresa" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ClienteEmpresaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('bloqueos') && (
            <TabsContent value="bloqueos" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <BloqueosTab user={user} />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('paquetes-continuo8') && (
            <TabsContent value="paquetes-continuo8" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <PaquetesContinuo8Tab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('bonos') && (
            <TabsContent value="bonos" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <BonosTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('consultas') && (
            <TabsContent value="consultas" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ConsultasTab />
              </Suspense>
            </TabsContent>
          )}

          {(hasTabPermission('comercial') || esColaborador) && (
            <TabsContent value="comercial" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ComercialTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('comercial-v2') && (
            <TabsContent value="comercial-v2" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ComercialV2Tab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('bonificaciones') && (
            <TabsContent value="bonificaciones" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <BonificacionesTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('sedes') && (
            <TabsContent value="sedes" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <SedesTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('configuracion') && (
            <TabsContent value="configuracion" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ConfiguracionTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('cuotas') && (
            <TabsContent value="cuotas" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <CuotasTab />
              </Suspense>
            </TabsContent>
          )}

          <TabsContent value="cobros-pendientes" className="animate-fade-in">
            <Suspense fallback={<div style={{padding:"20px",textAlign:"center",color:"#94a3b8"}}>Cargando cobros pendientes...</div>}>
              <DeudaPresencialPanel />
            </Suspense>
          </TabsContent>

          {/* LEGACY (Ann 2026-06-28): tab 'Cobros Presenciales' retirado de la vista
              (congelado · backend load-bearing vivo · pendiente migración a SSOT db.cobros) */}

          {hasTabPermission('validacion-caja') && (
            <TabsContent value="validacion-caja" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ValidacionCajaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('estadisticas') && (
            <TabsContent value="estadisticas" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <EstadisticasTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('hoja-contable') && (
            <TabsContent value="hoja-contable" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <HojaContableTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('contabilidad') && (
            <TabsContent value="contabilidad" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ContabilidadTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('laboral') && (
            <TabsContent value="laboral" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <LaboralTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('precios') && (
            <TabsContent value="precios" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <PreciosConsultaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('servicios') && (
            <TabsContent value="servicios" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ServiciosTab />
              </Suspense>
            </TabsContent>
          )}

          {(hasTabPermission('contable_agrupada') || ['hoja-contable', 'precios'].some(p => hasPermission(p, 'ver'))) && (
            <TabsContent value="contable_agrupada" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ContableBasicoTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('historial-notificaciones') && (
            <TabsContent value="historial-notificaciones" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <HistorialNotificacionesTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('configuracion-general') && (
            <TabsContent value="configuracion-general" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ConfiguracionGeneralTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('email-marketing') && (
            <TabsContent value="email-marketing" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <EmailMarketingTab />
              </Suspense>
            </TabsContent>
          )}
          {hasTabPermission('email-marketing-ia') && (
            <TabsContent value="email-marketing-ia" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <EmailMarketingIATab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('canal-videos') && (
            <TabsContent value="canal-videos" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <CanalVideosTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('fisiovision') && (
            <TabsContent value="fisiovision" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <FisioVisionTab />
              </Suspense>
            </TabsContent>
          )}

          {(hasTabPermission('contabilidad-2') || hasPermission('gestoria', 'ver')) && (
            <TabsContent value="contabilidad-2" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ContabilidadV2Tab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('cuadrante') && (
            <TabsContent value="cuadrante" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <CuadranteTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('cuadrante') && (
            <TabsContent value="global" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <CuadranteTab
                  vistaInicial="mes"
                  onNavigateToDay={(fecha) => {
                    // Guardar la fecha seleccionada para que Cuadrante la lea
                    localStorage.setItem('cuadrante_fecha_navegacion', fecha);
                    // Cambiar a la pestaña Cuadrante
                    handleTabChange('cuadrante');
                  }}
                />
              </Suspense>
            </TabsContent>
          )}

          {isAdmin && PLANIFICADORES.includes(String(user?.email || '').toLowerCase()) && (
            <TabsContent value="planificacion" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                {activeTab === 'planificacion' && <PlanificacionTab />}
              </Suspense>
            </TabsContent>
          )}
          {hasTabPermission('clinica') && (
            <TabsContent value="clinica" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                {activeTab === 'clinica' && <ClinicaTab key={`clinica-${clinicaKey}`} />}
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('taquillas') && (
            <TabsContent value="taquillas" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <TaquillasTab sedeActiva={sedeActiva} sedes={sedes} />
              </Suspense>
            </TabsContent>
          )}

          {(hasTabPermission('gestoria') || hasPermission('gestoria.control', 'ver')) && (
            <TabsContent value="gestoria" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ContabilidadTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('config_clinica') && (
            <TabsContent value="config_clinica" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ConfiguracionClinicaTab />
              </Suspense>
            </TabsContent>
          )}

          {(hasTabPermission('medicos_derivan') || hasTabPermission('config_clinica')) && (
            <TabsContent value="medicos_derivan" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <DerivadoresTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('tecnica') && (
            <TabsContent value="tecnica" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <TecnicaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('caja') && (
            <TabsContent value="caja" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ControlCajaTab />
              </Suspense>
            </TabsContent>
          )}

          {hasTabPermission('config_tecnica') && (
            <TabsContent value="config_tecnica" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <ConfiguracionTecnicaTab />
              </Suspense>
            </TabsContent>
          )}
          {/* MI PUESTO: zona privada por trabajador; sin gate de permisos porque
              todo el contenido sale del usuario logueado (nunca datos de otro) */}
          {!esColaborador && (
            <TabsContent value="mi-puesto" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <MiPuestoTab />
              </Suspense>
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="seguridad-vacia" className="animate-fade-in">
              <Suspense fallback={<TabLoader />}>
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <KeyRound className="w-6 h-6 text-slate-600" />
                    <h2 className="text-2xl font-extrabold text-slate-800 uppercase tracking-tight">Seguridad</h2>
                  </div>
                  <Tabs defaultValue="roles" className="w-full">
                    <TabsList>
                      <TabsTrigger value="roles">Roles y Permisos</TabsTrigger>
                      <TabsTrigger value="usuarios">Usuarios Staff</TabsTrigger>
                      <TabsTrigger value="accesos">Accesos IP</TabsTrigger>
                    </TabsList>
                    <TabsContent value="roles" className="mt-4">
                      <RolesTab />
                    </TabsContent>
                    <TabsContent value="usuarios" className="mt-4">
                      <UsuariosTab />
                    </TabsContent>
                    <TabsContent value="accesos" className="mt-4">
                      <SeguridadAccesosTab />
                    </TabsContent>
                  </Tabs>
                </div>
              </Suspense>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Modal de Cambiar Contraseña */}
      <CambiarPasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userEmail={user.email}
      />

      {/* Modal de Crear Notificación */}
      <CrearNotificacionModal
        open={showNotificacionModal}
        onClose={() => setShowNotificacionModal(false)}
        onNotificacionCreada={() => {
          // Aquí podríamos hacer algo cuando se cree una notificación
          console.log('Notificación creada');
        }}
      />
    </div>
  );
};

export default AdminDashboard;
